'use client'

import {
  ColumnDef,
  OnChangeFn,
  Row,
  RowSelectionState,
  SortingState,
  TableOptions,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table'
import { Badge } from './badge'
import { cn } from '../../lib/utils'
import { DataTablePagination } from './data-table-pagination'
import { Separator } from './separator'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip'

export const DataTableTitle = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return <h1 className={cn('text-lg', className)}>{children}</h1>
}

export const DataTableContent = ({
  children,
}: {
  children: React.ReactNode
}) => {
  return <div className='pb-4'>{children}</div>
}

export const DataTableContainer = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <div className={cn('border border-primary rounded-md', className)}>
      {children}
    </div>
  )
}

export const DataTableHeader = ({
  children,
  className,
  withDivider = true,
}: {
  children: React.ReactNode
  className?: string
  withDivider?: boolean
}) => {
  return (
    <>
      <div
        className={cn(
          'flex flex-row items-center justify-between px-4 py-3',
          className
        )}
      >
        {children}
      </div>
      {withDivider && <Separator className='bg-primary h-[1px]' />}
    </>
  )
}

export const DataTableDescription = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return <p className={cn('text-sm zinc-400', className)}>{children}</p>
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  emptyStateMessage?: string
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  CellWrapper?: React.FC<{ row: TData; children: React.ReactNode }>
}

export function DataTable<TData extends { id: string }, TValue>({
  emptyStateMessage = 'No se encontraron resultados.',
  columns,
  data,
  CellWrapper,
  withPagination = false,
  onClickRow,
  tableRowClassName,
  className,
  entityLabel,
  align = 'left',
  sorting,
  onSortingChange,
  rowSelection = {},
  onRowSelectionChange,
  enableRowSelection = false,
  enableMultiRowSelection,
  withSelectedCtx = false,
  selectedCtx,
  withHeader = true,
}: DataTableProps<TData, TValue> & {
  withHeader?: boolean
  tableRowClassName?: ((row: Row<TData>) => string) | string
  withPagination?: boolean
  onClickRow?: (row: Row<TData>) => void
  className?: string
  entityLabel?: string
  align?: 'center' | 'left'
  sorting?: SortingState
  onSortingChange?: OnChangeFn<SortingState>
  rowSelection?: RowSelectionState
  onRowSelectionChange?: OnChangeFn<RowSelectionState>
  enableRowSelection?: boolean
  withSelectedCtx?: boolean
  selectedCtx?: {
    name: string
    id: string
  }[]
} & Pick<TableOptions<TData>, 'enableMultiRowSelection'>) {
  const table = useReactTable({
    data,
    columns,
    onSortingChange,
    onRowSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: withPagination ? getPaginationRowModel() : undefined,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      rowSelection,
    },
    enableRowSelection,
    enableMultiRowSelection,
    ...(withSelectedCtx
      ? {
          getRowId: row => row.id,
        }
      : {}),
  })

  return (
    <div className={className}>
      <div className='rounded-md'>
        {withSelectedCtx && (
          <div
            className={cn(
              'min-h-10 bg-primary text-white w-full p-2 opacity-0 transition-all rounded',
              {
                'opacity-100': selectedCtx!.length > 0,
              }
            )}
          >
            {!!selectedCtx && (
              <div className='flex flex-row w-full space-x-2 items-center'>
                <p className='text-sm'>
                  {selectedCtx!.length < 2
                    ? `Una fila seleccionada`
                    : `${selectedCtx.length} filas seleccionadas`}{' '}
                </p>
                <div className='flex flex-row flex-wrap gap-2'>
                  {selectedCtx.map(selected => {
                    return (
                      <TooltipProvider key={selected.id}>
                        <Tooltip>
                          <TooltipTrigger>
                            <Badge
                              className='max-w-44 truncate'
                              variant={'secondary'}
                              key={selected.id}
                            >
                              {selected.name || '[Sin Nombre]'}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{selected.name}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        <Table className='relative'>
          {withHeader && (
            <TableHeader>
              {table.getHeaderGroups().map(headerGroup => (
                <TableRow className='border-0' key={headerGroup.id}>
                  {headerGroup.headers.map(header => {
                    return (
                      <TableHead
                        className={cn('', {
                          'text-center': align === 'center',
                        })}
                        key={header.id}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    )
                  })}
                </TableRow>
              ))}
            </TableHeader>
          )}

          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map(row => (
                <TableRow
                  className={cn(
                    tableRowClassName
                      ? typeof tableRowClassName === 'string'
                        ? tableRowClassName
                        : tableRowClassName(row)
                      : '',
                    'table-row'
                  )}
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={ev => {
                    if (onClickRow) {
                      onClickRow(row)
                    }
                  }}
                >
                  {row.getVisibleCells().map(cell => {
                    if (CellWrapper) {
                      return (
                        <TableCell className='relative' key={cell.id}>
                          <CellWrapper row={row.original}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </CellWrapper>
                        </TableCell>
                      )
                    }

                    return (
                      <TableCell key={cell.id}>
                        <div
                          className={cn({
                            '!pr-4 flex items-center justify-center':
                              align === 'center' &&
                              !!(cell.column.columnDef.meta as any)?.align,
                          })}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </div>
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className='h-24 text-center'
                >
                  {emptyStateMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {withPagination && (
        <DataTablePagination entityLabel={entityLabel!} table={table} />
      )}
    </div>
  )
}
